<!--
  @description: 时间周期配置组件
  @note: 负责时间周期模版的增删改查，用于定时任务的时间配置
-->
<template>
  <div class="time-cycle-config-container">
    <!-- 操作栏 -->
    <div class="header-toolbar">
      <div class="toolbar-left">
        <span class="info-text">配置时间周期模版，用于定时任务的时间规则设置</span>
      </div>
      <div class="toolbar-right">
        <el-button type="primary" icon="el-icon-plus" @click="handleTimeAdd">新增时间模版</el-button>
      </div>
    </div>

    <!-- 卡片列表 -->
    <div class="cards-container" v-loading="timeLoading">
      <div class="card-item" v-for="item in timeList" :key="item.id">
        <!-- 卡片头部 -->
        <div class="card-header">
          <div class="card-title">
            <div class="title-icon">
              <i class="el-icon-alarm-clock"></i>
            </div>
            <div class="title-text">
              <h3>{{ item.cycle_name }}</h3>
              <span class="card-code">周期ID: {{ item.id }}</span>
            </div>
          </div>
        </div>

        <!-- 卡片内容 - 时间配置 -->
        <div class="card-content">
          <div class="time-configs">
            <template v-if="getTimeConfigs(item).length > 0">
              <div class="time-config-item" v-for="(config, idx) in getTimeConfigs(item)" :key="idx">
                <el-tag :type="getTimeTypeTag(config.type)" size="small">
                  {{ getTimeTypeName(config.type) }}
                </el-tag>
                <span class="time-text">{{ formatTimeConfig(config) }}</span>
              </div>
            </template>
            <div v-else class="no-config">暂无配置</div>
          </div>
        </div>

        <!-- 卡片底部操作栏 -->
        <div class="card-footer">
          <div class="footer-left">
            <span class="create-time">
              <i class="el-icon-time"></i>
              {{ item.create_time || '-' }}
            </span>
          </div>
          <div class="footer-right">
            <el-button
              type="text"
              icon="el-icon-edit"
              @click="handleTimeEdit(item)"
              class="action-btn"
            >编辑</el-button>
            <el-button
              type="text"
              icon="el-icon-delete"
              @click="handleTimeDelete(item)"
              class="action-btn delete-btn"
            >删除</el-button>
          </div>
        </div>

        <!-- 科技感光效 -->
        <div class="card-glow"></div>
      </div>

      <!-- 空状态 -->
      <div v-if="!timeLoading && timeList.length === 0" class="empty-state">
        <i class="el-icon-alarm-clock"></i>
        <p>暂无时间模版</p>
        <el-button type="primary" @click="handleTimeAdd">立即添加</el-button>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-container" v-if="timeList.length > 0">
      <el-pagination
        @size-change="handleTimeSizeChange"
        @current-change="handleTimeCurrentChange"
        :current-page="timeQuery.pageNumber"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="timeQuery.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="timeQuery.total"
        background
      ></el-pagination>
    </div>

    <!-- 时间模版抽屉 -->
    <common-drawer
      :visible.sync="timeDrawerVisible"
      :title="timeDrawerTitle"
      size="1000px"
      @confirm="saveTime"
      @cancel="handleTimeCancel"
    >
      <el-form ref="timeForm" :model="timeForm" label-width="100px" class="drawer-form">
        <el-form-item label="模版名称" required>
          <el-input v-model="timeForm.cycle_name" clearable placeholder="请输入时间模版名称"></el-input>
        </el-form-item>
        <el-form-item label="时间配置" required>
          <div
            v-for="(item, index) in timeForm.cycle_config"
            :key="index"
            class="config-row"
          >
            <el-select
              v-model="item.type"
              placeholder="选择类型"
              clearable
              style="width: 120px; margin-right: 10px;"
            >
              <el-option
                v-for="type in typeList"
                :key="type.id"
                :label="type.name"
                :value="type.id"
              ></el-option>
            </el-select>
            <el-select
              v-model="item.time_config.month"
              placeholder="月"
              clearable
              style="width: 80px; margin-right: 10px;"
              :disabled="item.type !== 'year'"
            >
              <el-option
                v-for="month in monthList"
                :key="month.id"
                :label="month.name"
                :value="month.id"
              ></el-option>
            </el-select>
            <el-select
              v-model="item.time_config.week"
              placeholder="周(星期几)"
              clearable
              style="width: 120px; margin-right: 10px;"
              :disabled="item.type !== 'week'"
            >
              <el-option
                v-for="week in weekList"
                :key="week.id"
                :label="week.name"
                :value="week.id"
              ></el-option>
            </el-select>
            <el-input
              v-model="item.time_config.day"
              placeholder="日(几号)"
              type="number"
              style="width: 120px; margin-right: 10px;"
              :disabled="item.type !== 'year' && item.type !== 'month'"
            ></el-input>
            <el-time-select
              v-if="item.type !== 'fixed'"
              style="width: 200px; margin-right: 10px;"
              v-model="item.time_config.time"
              :picker-options="{
                start: '00:00',
                step: '00:05',
                end: '23:59'
              }"
              clearable
              :disabled="item.type !== 'year' && item.type !== 'month' && item.type !== 'week' && item.type !== 'day'"
              placeholder="选择时间"
              @change="changeTime(item, $event)"
            ></el-time-select>
            <el-date-picker
              v-else
              style="width: 200px; margin-right: 10px;"
              type="datetime"
              v-model="item.time_config.time"
              format="yyyy-MM-dd HH:mm"
              value-format="yyyy-MM-dd HH:mm"
              clearable
              placeholder="选择时间"
              @change="changeTime(item, $event)"
            ></el-date-picker>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="removeTimeConfig(index)"
            ></el-button>
          </div>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="addTimeConfig">添加时间配置</el-button>
          <div class="note">支持年、月、周、日、天、时、分的配置</div>
        </el-form-item>
      </el-form>
    </common-drawer>
  </div>
</template>

<script>
/**
 * @description 时间周期配置组件
 * @component TimeCycleConfig
 * @requires services/sendService/sendCycle - 时间周期相关API
 */
import {
  selectApi as selectSendCycleApi,
  updateApi as updateSendCycleApi,
  deleteApi as deleteSendCycleApi,
  addApi as addSendCycleApi
} from "@/services/sendService/sendCycle";
import CommonDrawer from "@/components/drawer/index.vue";
import { mapState } from "vuex";

export default {
  name: "TimeCycleConfig",
  components: {
    CommonDrawer
  },
  created() {
    // 组件创建时获取时间周期列表
    this.getTimeList();
  },
  data() {
    return {
      // 时间模版列表加载状态
      timeLoading: false,
      // 时间模版查询参数
      timeQuery: {
        pageNumber: 1,
        pageSize: 10,
        total: 0
      },
      // 时间模版列表数据
      timeList: [],
      // 抽屉可见性
      timeDrawerVisible: false,
      // 抽屉标题（新增/编辑）
      timeDrawerTitle: "",
      // 表单数据
      timeForm: {
        cycle_config: []
      },
      // 时间类型配置
      typeList: [
        { id: "day", name: "每天" },
        { id: "week", name: "每周" },
        { id: "month", name: "每月" },
        { id: "year", name: "每年" },
        { id: "fixed", name: "指定一次" }
      ],
      // 月份列表
      monthList: Array.from({ length: 12 }, (_, i) => ({
        id: String(i + 1),
        name: `${i + 1}月`
      })),
      // 星期列表
      weekList: [
        { id: "1", name: "星期一" },
        { id: "2", name: "星期二" },
        { id: "3", name: "星期三" },
        { id: "4", name: "星期四" },
        { id: "5", name: "星期五" },
        { id: "6", name: "星期六" },
        { id: "7", name: "星期日" }
      ]
    };
  },
  computed: {
    ...mapState(["userInfo"])
  },
  methods: {
    /**
     * @description 获取时间周期列表
     */
    async getTimeList() {
      this.timeLoading = true;
      const { data, code, count } = await selectSendCycleApi(this.timeQuery);
      if (code === 200) {
        this.timeList = data;
        this.timeQuery.total = count;
        setTimeout(() => {
          this.timeLoading = false;
        }, 300);
      }
    },

    /**
     * @description 页码改变事件
     * @param {number} page - 新的页码
     */
    handleTimeCurrentChange(page) {
      this.timeQuery.pageNumber = page;
      this.getTimeList();
    },

    /**
     * @description 每页条数改变事件
     * @param {number} size - 新的每页条数
     */
    handleTimeSizeChange(size) {
      this.timeQuery.pageSize = size;
      this.getTimeList();
    },

    /**
     * @description 新增时间模版
     */
    handleTimeAdd() {
      this.timeDrawerVisible = true;
      this.timeDrawerTitle = "新增时间模版";
      this.timeForm = {
        cycle_name: "",
        cycle_config: [{
          type: "",
          time_config: {
            time: "",
            week: "",
            day: "",
            month: ""
          }
        }]
      };
      this.$nextTick(() => {
        this.$refs.timeForm && this.$refs.timeForm.clearValidate();
      });
    },

    /**
     * @description 编辑时间模版
     * @param {Object} row - 时间模版数据
     */
    handleTimeEdit(row) {
      this.timeDrawerVisible = true;
      this.timeDrawerTitle = "编辑时间模版";
      this.timeForm = JSON.parse(JSON.stringify(row));
      
      // 解析配置数据
      if (typeof this.timeForm.cycle_config === "string") {
        try {
          this.timeForm.cycle_config = JSON.parse(this.timeForm.cycle_config);
        } catch (error) {
          this.timeForm.cycle_config = [];
        }
      }
      
      if (!Array.isArray(this.timeForm.cycle_config)) {
        this.timeForm.cycle_config = [];
      }
      
      this.$nextTick(() => {
        this.$refs.timeForm && this.$refs.timeForm.clearValidate();
      });
    },

    /**
     * @description 删除时间模版
     * @param {Object} row - 时间模版数据
     */
    handleTimeDelete(row) {
      this.$confirm(`此操作将永久删除'${row.cycle_name}'，是否继续?`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        deleteSendCycleApi({ id: row.id }).then(res => {
          if (res.code === 200) {
            this.$message.success("删除成功!");
            this.getTimeList();
          }
        });
      }).catch(() => {});
    },

    /**
     * @description 添加时间配置项
     */
    addTimeConfig() {
      this.timeForm.cycle_config.push({
        type: "",
        time_config: {
          time: "",
          week: "",
          day: "",
          month: ""
        }
      });
    },

    /**
     * @description 删除时间配置项
     * @param {number} index - 配置项索引
     */
    removeTimeConfig(index) {
      this.timeForm.cycle_config.splice(index, 1);
    },

    /**
     * @description 时间改变事件
     * @param {Object} item - 配置项
     * @param {string} event - 时间值
     */
    changeTime(item, event) {
      if (event && !event.includes(":00")) {
        item.time_config.time = event + ":00";
      }
    },

    /**
     * @description 保存时间模版（新增或编辑）
     */
    saveTime() {
      if (!this.timeForm.cycle_name || !this.timeForm.cycle_config) {
        this.$message.error("请填写必填字段");
        return;
      }

      const data = {
        cycle_name: this.timeForm.cycle_name,
        cycle_config: JSON.stringify(this.timeForm.cycle_config)
      };

      if (this.timeDrawerTitle === "新增时间模版") {
        // 新增时间模版
        addSendCycleApi(data).then(res => {
          if (res.code === 200) {
            this.$message.success("新增成功!");
            this.timeDrawerVisible = false;
            this.getTimeList();
          }
        });
      } else {
        // 编辑时间模版
        updateSendCycleApi({ ...data, id: this.timeForm.id }).then(res => {
          if (res.code === 200) {
            this.$message.success("修改成功!");
            this.timeDrawerVisible = false;
            this.getTimeList();
          }
        });
      }
    },

    /**
     * @description 取消操作
     */
    handleTimeCancel() {
      this.timeDrawerVisible = false;
    },

    /**
     * @description 获取时间配置数组
     * @param {Object} item - 时间模版数据
     * @returns {Array} 时间配置数组
     */
    getTimeConfigs(item) {
      if (typeof item.cycle_config === "string") {
        try {
          return JSON.parse(item.cycle_config);
        } catch (error) {
          return [];
        }
      }
      return Array.isArray(item.cycle_config) ? item.cycle_config : [];
    },

    /**
     * @description 获取时间类型名称
     * @param {string} type - 时间类型ID
     * @returns {string} 时间类型名称
     */
    getTimeTypeName(type) {
      const item = this.typeList.find(t => t.id === type);
      return item ? item.name : type;
    },

    /**
     * @description 获取时间类型Tag颜色
     * @param {string} type - 时间类型ID
     * @returns {string} Tag类型
     */
    getTimeTypeTag(type) {
      const tagMap = {
        day: "",
        week: "success",
        month: "warning",
        year: "danger",
        fixed: "info"
      };
      return tagMap[type] || "";
    },

    /**
     * @description 格式化时间配置显示
     * @param {Object} config - 时间配置对象
     * @returns {string} 格式化后的时间字符串
     */
    formatTimeConfig(config) {
      const { type, time_config } = config;
      let result = "";
      
      if (type === "year" && time_config.month) {
        result += `${time_config.month}月`;
      }
      if ((type === "year" || type === "month") && time_config.day) {
        result += `${time_config.day}日`;
      }
      if (type === "week" && time_config.week) {
        const week = this.weekList.find(w => w.id === time_config.week);
        result += week ? week.name : "";
      }
      if (time_config.time) {
        result += ` ${time_config.time}`;
      }
      
      return result || "未配置";
    }
  }
};
</script>

<style scoped lang="less">
.time-cycle-config-container {
  padding: 20px 0;
  min-height: calc(100vh - 150px);

  // 头部工具栏
  .header-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .toolbar-left {
      flex: 1;

      .info-text {
        color: #666;
        font-size: 14px;
      }
    }

    .toolbar-right {
      margin-left: 20px;

      .el-button--primary {
        background-color: #165DFF;
        border-color: #165DFF;

        &:hover,
        &:focus {
          background-color: #4080FF;
          border-color: #4080FF;
        }
      }
    }
  }

  // 卡片容器
  .cards-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 24px;
    min-height: 400px;
    max-height: 500px;
    overflow-y: auto;
    align-content: start;
    position: relative;

    // 单个卡片
    .card-item {
      position: relative;
      background: #fff;
      border-radius: 12px;
      padding: 24px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      overflow: hidden;
      border: 1px solid #f0f0f0;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(22, 93, 255, 0.2);

        .card-glow {
          opacity: 1;
        }
      }

      // 科技感光效
      .card-glow {
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(
          circle,
          rgba(22, 93, 255, 0.1) 0%,
          transparent 70%
        );
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      }

      // 卡片头部
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 16px;
        border-bottom: 2px solid #f0f0f0;

        .card-title {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;

          .title-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            background: #165DFF;
            display: flex;
            align-items: center;
            justify-content: center;

            i {
              font-size: 24px;
              color: #fff;
            }
          }

          .title-text {
            flex: 1;

            h3 {
              margin: 0 0 6px 0;
              font-size: 18px;
              font-weight: 600;
              color: #333;
              line-height: 1.2;
            }

            .card-code {
              font-size: 12px;
              color: #999;
              font-family: "Courier New", monospace;
              background: #f5f5f5;
              padding: 2px 8px;
              border-radius: 4px;
              display: inline-block;
            }
          }
        }
      }

      // 卡片内容
      .card-content {
        margin-bottom: 20px;

        .time-configs {
          .time-config-item {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 10px;
            padding: 8px 12px;
            background: #f5f7fa;
            border-radius: 8px;

            .time-text {
              font-size: 14px;
              color: #333;
            }
          }

          .no-config {
            color: #999;
            font-size: 14px;
            text-align: center;
            padding: 20px;
          }
        }
      }

      // 卡片底部
      .card-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;

        .footer-left {
          .create-time {
            font-size: 12px;
            color: #999;
            display: flex;
            align-items: center;
            gap: 4px;

            i {
              font-size: 14px;
            }
          }
        }

        .footer-right {
          display: flex;
          gap: 8px;

          .action-btn {
            padding: 8px 16px;
            font-size: 13px;

            &:hover {
              color: #fa709a;
            }

            &.delete-btn:hover {
              color: #f56c6c;
            }
          }
        }
      }
    }

    // 空状态
    .empty-state {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      text-align: center;

      i {
        font-size: 80px;
        color: #ddd;
        margin-bottom: 20px;
      }

      p {
        font-size: 16px;
        color: #999;
        margin-bottom: 20px;
      }
    }
  }

  // 分页
  .pagination-container {
    margin-top: 30px;
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: center;
  }

  // 抽屉表单
  .drawer-form {
    padding: 20px 0;

    .config-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      flex-wrap: wrap;
      gap: 8px;
    }

    .note {
      margin-top: 10px;
      font-size: 12px;
      color: #999;
    }
  }
}

// 响应式设计
@media (max-width: 1400px) {
  .time-cycle-config-container {
    .cards-container {
      grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)) !important;
    }
  }
}

@media (max-width: 768px) {
  .time-cycle-config-container {
    .header-toolbar {
      flex-direction: column;
      gap: 15px;

      .toolbar-left,
      .toolbar-right {
        width: 100%;
        margin: 0;
      }
    }

    .cards-container {
      grid-template-columns: 1fr !important;
    }

    .drawer-form {
      .config-row {
        flex-direction: column;
        width: 100%;

        > * {
          width: 100% !important;
        }
      }
    }
  }
}
</style>
